/**
 * The cascaded shadow-map lookup, shared by every surface that needs to sit in
 * the terrain's own shadow.
 *
 * `LayeredRenderer` renders the cascades; `TerrainMaterial` and
 * `OceanRenderer` both sample them, and before this file existed both carried
 * their own verbatim copy of the uniform block, the GLSL lookup, the bias/PCF
 * constants, the 1x1 dummy texture and the TypeScript setter that uploads a
 * frame's cascades. Five copies of the same thing in two files: a change to
 * the bias, the tap count or the cascade count had to be made twice and
 * silently half-applied if it wasn't. There is one copy now.
 *
 * The pieces are kept separate so a consumer can place them itself — the
 * uniform declarations have to sit at the top of a fragment shader, while the
 * function has to come after whatever `cameraPosition` Three.js injects.
 */
import * as THREE from 'three';
import type { ShadowCascade } from '../LayeredRenderer';
import { glslFloat } from './common.glsl';

/**
 * Number of cascades the shader can sample. Fixed rather than configurable:
 * GLSL ES restricts dynamic indexing of sampler arrays (see the GLSL below),
 * so every cascade needs its own named uniform and its own arm of the select
 * chain. Changing this means editing the GLSL, which is exactly the kind of
 * paired edit the old duplication made easy to get half-right.
 */
export const SHADOW_CASCADE_COUNT = 4;

/** World-metre depth bias at grazing incidence. */
const SHADOW_BIAS_MAX_M = 1.2;
/** World-metre depth bias at normal incidence. */
const SHADOW_BIAS_MIN_M = 0.3;
/** PCF tap count (3x3 kernel). */
const SHADOW_PCF = 9;

/**
 * Uniform declarations for the cascade lookup. Include near the top of a
 * fragment shader, before `GLSL_SHADOW_CASCADE`.
 */
export const GLSL_SHADOW_CASCADE_UNIFORMS = /* glsl */ `
uniform int uShadowCascadeCount;
uniform float uShadowFade;
uniform vec4 uShadowSplits;
uniform vec4 uShadowRanges;
uniform mat4 uShadowMatrix0;
uniform mat4 uShadowMatrix1;
uniform mat4 uShadowMatrix2;
uniform mat4 uShadowMatrix3;
uniform sampler2D uShadowMap0;
uniform sampler2D uShadowMap1;
uniform sampler2D uShadowMap2;
uniform sampler2D uShadowMap3;
uniform vec2 uShadowTexel0;
uniform vec2 uShadowTexel1;
uniform vec2 uShadowTexel2;
uniform vec2 uShadowTexel3;
`;

/**
 * `terrainSelfShadow(renderPosition, N, L)` -> 1 fully lit, 0 fully shadowed.
 *
 * Picks a cascade by view distance, projects the fragment into it, and
 * compares against the depth captured from the light's own point of view.
 *
 * Named per-cascade uniforms rather than a sampler array: GLSL ES restricts
 * dynamic indexing of sampler arrays, and the cascade a fragment needs is not
 * the same across a triangle near a split boundary, so the index is not safe
 * to treat as dynamically uniform.
 *
 * No early 'return': an HLSL cross-compile (this project ships on ANGLE/D3D11)
 * turns an early return ahead of a texture-sampling loop into a synthesized
 * "hasn't returned yet" guard around that loop, and warns (X3595, "gradient
 * instruction used in a loop with varying iteration") that the implicit
 * texture2D() derivative is then undefined for threads that already exited. A
 * single flat function body with nested 'if's never creates that guard.
 *
 * Requires `cameraPosition`, which Three.js injects into every fragment
 * shader, and `GLSL_SHADOW_CASCADE_UNIFORMS` above it.
 */
export const GLSL_SHADOW_CASCADE = /* glsl */ `
const float SHADOW_BIAS_MAX_M = ${glslFloat(SHADOW_BIAS_MAX_M)};
const float SHADOW_BIAS_MIN_M = ${glslFloat(SHADOW_BIAS_MIN_M)};
const float SHADOW_PCF = ${glslFloat(SHADOW_PCF)};

float terrainSelfShadow(vec3 renderPosition, vec3 N, vec3 L) {
  float selfShadow = 1.0;
  if (uShadowCascadeCount > 0) {
    float viewDistance = length(cameraPosition - renderPosition);
    int idx = 0;
    if (viewDistance > uShadowSplits.x) idx = 1;
    if (viewDistance > uShadowSplits.y) idx = 2;
    if (viewDistance > uShadowSplits.z) idx = 3;
    if (idx >= uShadowCascadeCount) idx = uShadowCascadeCount - 1;

    vec4 shadowClip = idx == 0 ? uShadowMatrix0 * vec4(renderPosition, 1.0)
                     : idx == 1 ? uShadowMatrix1 * vec4(renderPosition, 1.0)
                     : idx == 2 ? uShadowMatrix2 * vec4(renderPosition, 1.0)
                     : uShadowMatrix3 * vec4(renderPosition, 1.0);
    vec3 proj = shadowClip.xyz / shadowClip.w * 0.5 + 0.5;

    if (proj.x > 0.0 && proj.x < 1.0 && proj.y > 0.0 && proj.y < 1.0 && proj.z > 0.0 && proj.z < 1.0) {
      vec2 texel = idx == 0 ? uShadowTexel0 : idx == 1 ? uShadowTexel1 : idx == 2 ? uShadowTexel2 : uShadowTexel3;
      // Bias is authored in world metres and converted to this cascade's NDC
      // depth units here (1/(far-near)), so it stays constant on the ground
      // regardless of which cascade the fragment landed in. A fixed NDC bias
      // was correct on the near cascade and ~30x too large on the far one.
      float rangeInv = idx == 0 ? uShadowRanges.x : idx == 1 ? uShadowRanges.y : idx == 2 ? uShadowRanges.z : uShadowRanges.w;
      float bias = max(SHADOW_BIAS_MAX_M * (1.0 - dot(N, L)), SHADOW_BIAS_MIN_M) * rangeInv;
      float lit = 0.0;
      for (int dx = -1; dx <= 1; dx++) {
        for (int dy = -1; dy <= 1; dy++) {
          vec2 uv = proj.xy + vec2(float(dx), float(dy)) * texel;
          float depth = idx == 0 ? texture2D(uShadowMap0, uv).r
                       : idx == 1 ? texture2D(uShadowMap1, uv).r
                       : idx == 2 ? texture2D(uShadowMap2, uv).r
                       : texture2D(uShadowMap3, uv).r;
          lit += (proj.z - bias <= depth) ? 1.0 : 0.0;
        }
      }
      selfShadow = lit / SHADOW_PCF;
    }
  }
  // Fades toward "fully lit" at high altitude rather than snapping off: see
  // LayeredRenderer.adaptShadowDistance for why — the cascades themselves keep
  // rendering right up to the point they're disabled, so an instant cut would
  // still pop.
  return mix(1.0, selfShadow, uShadowFade);
}
`;

/** TypeScript view of the uniforms `GLSL_SHADOW_CASCADE_UNIFORMS` declares. */
export interface ShadowCascadeUniforms {
  /** Active cascade count, 0 disables the lookup entirely. */
  uShadowCascadeCount: THREE.IUniform<number>;
  /** Self-shadow strength, 0..1 — fades toward "fully lit" with altitude. */
  uShadowFade: THREE.IUniform<number>;
  /** Each cascade's half-width, metres; used to pick a cascade by view distance. */
  uShadowSplits: THREE.IUniform<THREE.Vector4>;
  /** `1/(camera.far - camera.near)` per cascade, converting the shader's world-metre bias into that cascade's NDC depth units. */
  uShadowRanges: THREE.IUniform<THREE.Vector4>;
  uShadowMatrix0: THREE.IUniform<THREE.Matrix4>;
  uShadowMatrix1: THREE.IUniform<THREE.Matrix4>;
  uShadowMatrix2: THREE.IUniform<THREE.Matrix4>;
  uShadowMatrix3: THREE.IUniform<THREE.Matrix4>;
  uShadowMap0: THREE.IUniform<THREE.Texture>;
  uShadowMap1: THREE.IUniform<THREE.Texture>;
  uShadowMap2: THREE.IUniform<THREE.Texture>;
  uShadowMap3: THREE.IUniform<THREE.Texture>;
  /** 1/width, 1/height of each cascade's map, for the PCF taps. */
  uShadowTexel0: THREE.IUniform<THREE.Vector2>;
  uShadowTexel1: THREE.IUniform<THREE.Vector2>;
  uShadowTexel2: THREE.IUniform<THREE.Vector2>;
  uShadowTexel3: THREE.IUniform<THREE.Vector2>;
}

/**
 * 1x1 placeholder bound to unused cascade sampler slots. WebGL wants an actual
 * texture on every sampler uniform it uploads even when the shader branches
 * around ever sampling it; one shared 1-pixel texture is cheaper than building
 * four real ones per body just to leave them unused most of the time.
 */
let dummyShadowTexture: THREE.Texture | null = null;
export function getDummyShadowTexture(): THREE.Texture {
  if (!dummyShadowTexture) {
    dummyShadowTexture = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
    dummyShadowTexture.needsUpdate = true;
  }
  return dummyShadowTexture;
}

/** Fresh, inactive cascade uniforms. Spread into a material's uniform object. */
export function createShadowCascadeUniforms(): ShadowCascadeUniforms {
  return {
    uShadowCascadeCount: { value: 0 },
    uShadowFade: { value: 1 },
    uShadowSplits: { value: new THREE.Vector4() },
    uShadowRanges: { value: new THREE.Vector4() },
    uShadowMatrix0: { value: new THREE.Matrix4() },
    uShadowMatrix1: { value: new THREE.Matrix4() },
    uShadowMatrix2: { value: new THREE.Matrix4() },
    uShadowMatrix3: { value: new THREE.Matrix4() },
    uShadowMap0: { value: getDummyShadowTexture() },
    uShadowMap1: { value: getDummyShadowTexture() },
    uShadowMap2: { value: getDummyShadowTexture() },
    uShadowMap3: { value: getDummyShadowTexture() },
    uShadowTexel0: { value: new THREE.Vector2() },
    uShadowTexel1: { value: new THREE.Vector2() },
    uShadowTexel2: { value: new THREE.Vector2() },
    uShadowTexel3: { value: new THREE.Vector2() },
  };
}

/**
 * Uploads one frame's cascades (from `LayeredRenderer.getShadowCascades`).
 * An empty array disables self-shadowing entirely (low quality, or the
 * player's toggle) rather than leaving stale cascades bound.
 */
export function setShadowCascadeUniforms(
  u: ShadowCascadeUniforms,
  cascades: readonly ShadowCascade[],
): void {
  u.uShadowCascadeCount.value = cascades.length;
  const matrices = [u.uShadowMatrix0, u.uShadowMatrix1, u.uShadowMatrix2, u.uShadowMatrix3];
  const maps = [u.uShadowMap0, u.uShadowMap1, u.uShadowMap2, u.uShadowMap3];
  const texels = [u.uShadowTexel0, u.uShadowTexel1, u.uShadowTexel2, u.uShadowTexel3];
  for (let i = 0; i < SHADOW_CASCADE_COUNT; i++) {
    const cascade = cascades[i];
    if (!cascade) continue;
    u.uShadowSplits.value.setComponent(i, cascade.splitDistance);
    // 1/(far-near) converts the shader's world-metre depth bias into this
    // cascade's NDC depth units, so a single metre bias behaves the same on
    // every cascade no matter how wide its depth span is.
    u.uShadowRanges.value.setComponent(i, 1 / Math.max(cascade.camera.far - cascade.camera.near, 1e-6));
    matrices[i].value.copy(cascade.matrix);
    if (cascade.target.depthTexture) maps[i].value = cascade.target.depthTexture;
    texels[i].value.set(1 / cascade.target.width, 1 / cascade.target.height);
  }
}

/** See `LayeredRenderer.adaptShadowDistance` for why the fade is separate and continuous. */
export function setShadowFadeUniform(u: ShadowCascadeUniforms, fade: number): void {
  u.uShadowFade.value = fade;
}
